import React, { useState } from "react";
import { submitLead, errMessage } from "@/lib/api";
import { PRODUCTS } from "@/data/site";
import { Loader2, Check, ArrowRight } from "lucide-react";

export default function QuickEnquiry({ compact = false, defaultProduct }) {
  const [form, setForm] = useState({ name: "", mobile: "", city: "", loan_product: defaultProduct || PRODUCTS[0].name, loan_amount: "" });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const on = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  async function onSubmit(e) {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !/^\d{10}$/.test(form.mobile)) {
      setError("Please enter your full name and a valid 10-digit mobile number.");
      return;
    }
    setBusy(true);
    try {
      const payload = { ...form, loan_amount: form.loan_amount ? Number(form.loan_amount) : undefined };
      const r = await submitLead(payload);
      setResult(r);
    } catch (err) {
      setError(errMessage(err));
    } finally { setBusy(false); }
  }

  if (result) {
    return (
      <div data-testid="enquiry-success" className="rounded-3xl bg-white/95 backdrop-blur-xl border border-emerald-200/60 p-6 md:p-8 shadow-xl">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-emerald-100 grid place-items-center"><Check className="text-emerald-700" size={20}/></div>
          <p className="font-display text-2xl text-slate-900">Enquiry received.</p>
        </div>
        <p className="mt-4 text-slate-600">Your application ID is</p>
        <p data-testid="application-id" className="font-mono text-lg mt-1 text-emerald-800">{result.application_id}</p>
        <p className="mt-4 text-sm text-slate-500">Save this ID to track your status. Our advisor will call within 24 hours.</p>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} data-testid="quick-enquiry-form" className={`rounded-3xl bg-white/95 backdrop-blur-xl border border-white/70 shadow-2xl shadow-emerald-900/5 ${compact ? 'p-5' : 'p-6 md:p-8'}`}>
      <p className="chapter-index text-emerald-700">Get Started</p>
      <p className="font-display text-2xl md:text-3xl mt-1 text-slate-900">Apply in under 60 seconds.</p>
      <div className="mt-5 grid gap-3">
        <input data-testid="enq-name" required value={form.name} onChange={on('name')} placeholder="Full name" className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm focus:border-emerald-500 outline-none" />
        <input data-testid="enq-mobile" required value={form.mobile} onChange={on('mobile')} inputMode="numeric" pattern="\d{10}" placeholder="10-digit mobile" className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm focus:border-emerald-500 outline-none" />
        <div className="grid grid-cols-2 gap-3">
          <input data-testid="enq-city" value={form.city} onChange={on('city')} placeholder="City" className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm focus:border-emerald-500 outline-none" />
          <input data-testid="enq-amount" value={form.loan_amount} onChange={on('loan_amount')} inputMode="numeric" placeholder="Loan amount (₹)" className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm focus:border-emerald-500 outline-none" />
        </div>
        <select data-testid="enq-product" value={form.loan_product} onChange={on('loan_product')} className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm focus:border-emerald-500 outline-none">
          {PRODUCTS.map((p) => <option key={p.slug} value={p.name}>{p.name}</option>)}
        </select>
      </div>
      {error && <p data-testid="enq-error" className="mt-3 text-sm text-red-600">{error}</p>}
      <button data-testid="enq-submit" disabled={busy} className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-slate-900 hover:bg-slate-800 text-white py-3.5 text-sm font-medium transition-colors">
        {busy ? <Loader2 className="animate-spin" size={16}/> : <>Apply for a Loan <ArrowRight size={16}/></>}
      </button>
      <p className="mt-3 text-[11px] text-slate-500 leading-relaxed">By submitting, you agree to be contacted by Rightway Finance. Loan approval is subject to lender policies. No guaranteed approval.</p>
    </form>
  );
}
